import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  ChevronLeft,
  ChevronRight,
  Calendar,
  ZoomIn,
  ZoomOut,
  Plus,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { UploadImageModal } from "@/components/PatientConsultation/components/UploadImageModal";

interface ImageVital {
  id: string;
  label: string;
  description: string;
  imagePreview: string;
  timestamp: Date;
  fileName?: string;
  isVideo?: boolean;
  videoUrl?: string;
}

interface ImageVitalsDetailModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  imageVitals: ImageVital[];
  initialIndex?: number;
  isDarkMode?: boolean;
  onSaveVitals?: (vitalData: any) => void;
  bodyAreaLabel?: string;
  patientId?: string;
}

export function ImageVitalsDetailModal({
  isOpen,
  onOpenChange,
  imageVitals,
  initialIndex = 0,
  isDarkMode = false,
  onSaveVitals,
  bodyAreaLabel,
  patientId,
}: ImageVitalsDetailModalProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [zoom, setZoom] = useState(1);

  // Sort images by timestamp (oldest to newest for progression view)
  const sortedImages = [...imageVitals].sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
  );

  const currentImage = sortedImages[currentIndex];
  const hasMultipleImages = sortedImages.length > 1;

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setZoom(1);
    }
  };

  const handleNext = () => {
    if (currentIndex < sortedImages.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setZoom(1);
    }
  };

  const handleZoomIn = () => {
    setZoom(Math.min(zoom + 0.25, 3));
  };

  const handleZoomOut = () => {
    setZoom(Math.max(zoom - 0.25, 0.5));
  };

  if (!currentImage) {
    return null;
  }

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getDaysSince = (date: Date) => {
    const now = new Date();
    const diffTime = now.getTime() - new Date(date).getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    return `${diffDays} days ago`;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          "max-w-5xl max-h-[90vh] overflow-hidden flex flex-col",
          isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white",
        )}
      >
        <DialogHeader>
          <DialogTitle
            className={cn(
              "flex items-center gap-3",
              isDarkMode ? "text-white" : "text-gray-900",
            )}
          >
            <span>{currentImage.label} - Progress Tracking</span>
            {hasMultipleImages && (
              <span
                className={cn(
                  "text-sm font-normal",
                  isDarkMode ? "text-gray-400" : "text-gray-500",
                )}
              >
                ({currentIndex + 1} of {sortedImages.length})
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex gap-4">
          {/* Main Image Area - Left Side */}
          <div className="flex-1 flex flex-col overflow-auto">
            {/* Current Media Display */}
            <div
              className={cn(
                "rounded-lg overflow-hidden border mb-4 flex-1",
                isDarkMode
                  ? "border-gray-600 bg-gray-900"
                  : "border-gray-200 bg-gray-50",
              )}
            >
              <div className="relative overflow-auto h-full flex items-center justify-center p-4">
                {currentImage.isVideo && currentImage.videoUrl ? (
                  <video
                    src={currentImage.videoUrl}
                    controls
                    className="max-w-full max-h-full object-contain"
                  >
                    Your browser does not support the video tag.
                  </video>
                ) : (
                  <img
                    src={currentImage.imagePreview}
                    alt={currentImage.label}
                    className="max-w-full max-h-full object-contain transition-transform duration-200"
                    style={{ transform: `scale(${zoom})` }}
                  />
                )}
              </div>
            </div>

            {/* Image Info and Zoom Controls */}
            <div
              className={cn(
                "p-4 rounded-lg border",
                isDarkMode
                  ? "bg-gray-700 border-gray-600"
                  : "bg-gray-50 border-gray-200",
              )}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <Calendar
                    className={cn(
                      "w-4 h-4",
                      isDarkMode ? "text-gray-400" : "text-gray-500",
                    )}
                  />
                  <div>
                    <p
                      className={cn(
                        "text-sm font-medium",
                        isDarkMode ? "text-white" : "text-gray-900",
                      )}
                    >
                      {formatDate(currentImage.timestamp)}
                    </p>
                    <p
                      className={cn(
                        "text-xs",
                        isDarkMode ? "text-gray-400" : "text-gray-500",
                      )}
                    >
                      {getDaysSince(currentImage.timestamp)}
                    </p>
                  </div>
                </div>

                {/* Zoom Controls - Only for images */}
                {!currentImage.isVideo && (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleZoomOut}
                      disabled={zoom <= 0.5}
                      className={cn(
                        isDarkMode
                          ? "bg-gray-600 border-gray-500 hover:bg-gray-550"
                          : "",
                      )}
                    >
                      <ZoomOut className="w-4 h-4" />
                    </Button>
                    <span
                      className={cn(
                        "text-sm font-medium min-w-[60px] text-center",
                        isDarkMode ? "text-gray-300" : "text-gray-600",
                      )}
                    >
                      {Math.round(zoom * 100)}%
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleZoomIn}
                      disabled={zoom >= 3}
                      className={cn(
                        isDarkMode
                          ? "bg-gray-600 border-gray-500 hover:bg-gray-550"
                          : "",
                      )}
                    >
                      <ZoomIn className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>

              {currentImage.description && (
                <p
                  className={cn(
                    "text-sm mt-2",
                    isDarkMode ? "text-gray-300" : "text-gray-700",
                  )}
                >
                  {currentImage.description}
                </p>
              )}
            </div>
          </div>

          {/* Timeline Sidebar - Right Side */}
          <div
            className={cn(
              "w-80 flex flex-col rounded-lg border",
              isDarkMode
                ? "bg-gray-700 border-gray-600"
                : "bg-gray-50 border-gray-200",
            )}
          >
            <div className="p-4 border-b border-gray-600">
              <h4
                className={cn(
                  "text-sm font-semibold",
                  isDarkMode ? "text-white" : "text-gray-900",
                )}
              >
                Progress Timeline
              </h4>
              <p
                className={cn(
                  "text-xs mt-1",
                  isDarkMode ? "text-gray-400" : "text-gray-500",
                )}
              >
                {sortedImages.length}{" "}
                {sortedImages.length === 1 ? "item" : "items"}
              </p>

              {/* Add Image Button */}
              {onSaveVitals && bodyAreaLabel && (
                <UploadImageModal
                  trigger={
                    <Button
                      size="sm"
                      className="w-full mt-3 bg-gray-900 hover:bg-gray-800 text-white gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Add Image
                    </Button>
                  }
                  mode="vitals"
                  forcedBodyArea={bodyAreaLabel}
                  onSave={(imageData) => {
                    if (onSaveVitals) {
                      const vitalReading = {
                        type: "image",
                        data: {
                          value: imageData.title,
                          unit: "",
                          imagePreview: imageData.imagePreview,
                          fileName: imageData.fileName,
                        },
                      };
                      onSaveVitals(vitalReading);
                    }
                  }}
                  patientId={patientId}
                />
              )}
            </div>

            {/* Single Item Message */}
            {!hasMultipleImages && (
              <div className="p-4">
                <p
                  className={cn(
                    "text-xs text-center",
                    isDarkMode ? "text-gray-400" : "text-gray-500",
                  )}
                >
                  Add more images or videos to track progress over time
                </p>
              </div>
            )}

            {/* Timeline Scrollable Area - Only show if multiple images */}
            {hasMultipleImages && (
              <>
                <div className="flex-1 overflow-auto p-4">
                  <div className="space-y-2">
                    {sortedImages.map((img, index) => (
                      <div
                        key={img.id}
                        onClick={() => {
                          setCurrentIndex(index);
                          setZoom(1);
                        }}
                        className={cn(
                          "flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-all",
                          index === currentIndex
                            ? isDarkMode
                              ? "bg-blue-900/40 border-blue-700"
                              : "bg-blue-50 border-blue-300"
                            : isDarkMode
                              ? "hover:bg-gray-600 border-transparent"
                              : "hover:bg-gray-100 border-transparent",
                          "border",
                        )}
                      >
                        {/* Thumbnail */}
                        <div
                          className={cn(
                            "w-14 h-14 rounded overflow-hidden border flex-shrink-0",
                            isDarkMode ? "border-gray-600" : "border-gray-300",
                          )}
                        >
                          {/* Always show image thumbnail (extracted frame for videos) */}
                          <img
                            src={img.imagePreview}
                            alt={img.label}
                            className="w-full h-full object-cover"
                          />
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <p
                            className={cn(
                              "text-xs font-medium truncate",
                              isDarkMode ? "text-white" : "text-gray-900",
                            )}
                          >
                            {new Date(img.timestamp).toLocaleDateString(
                              "en-US",
                              {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              },
                            )}
                          </p>
                          <p
                            className={cn(
                              "text-xs",
                              isDarkMode ? "text-gray-400" : "text-gray-500",
                            )}
                          >
                            {getDaysSince(img.timestamp)}
                          </p>
                        </div>

                        {/* Current indicator */}
                        {index === currentIndex && (
                          <div className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Navigation Buttons */}
                <div className="p-4 border-t border-gray-600">
                  <div className="flex items-center justify-between gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handlePrevious}
                      disabled={currentIndex === 0}
                      className={cn(
                        "flex-1",
                        isDarkMode
                          ? "bg-gray-600 border-gray-500 hover:bg-gray-550"
                          : "",
                      )}
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      Prev
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleNext}
                      disabled={currentIndex === sortedImages.length - 1}
                      className={cn(
                        "flex-1",
                        isDarkMode
                          ? "bg-gray-600 border-gray-500 hover:bg-gray-550"
                          : "",
                      )}
                    >
                      Next
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
